import { useState, useRef, useCallback, useImperativeHandle, forwardRef, useEffect } from "react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { message } from "antd";
import styles from './index.module.scss'

const ImageCropper = forwardRef(({ url, refImage, aspect }, ref) => {
    const [crop, setCrop] = useState();
    const [completedCrop, setCompletedCrop] = useState(null);
    const imgRef = useRef(null);

    useImperativeHandle(ref, () => ({
        handleConfirm,
    }));

    /** 抽离最大裁剪框计算方法（用于 onLoad + aspect 改变时） */
    const getMaxCrop = (img, aspectRatio) => {
        const renderedWidth = img.width;
        const renderedHeight = img.height;

        let cropWidth, cropHeight;

        if (renderedWidth / renderedHeight > aspectRatio) {
            cropHeight = renderedHeight;
            cropWidth = cropHeight * aspectRatio;
        } else {
            cropWidth = renderedWidth;
            cropHeight = cropWidth / aspectRatio;
        }

        return {
            unit: "px",
            x: (renderedWidth - cropWidth) / 2,
            y: (renderedHeight - cropHeight) / 2,
            width: cropWidth,
            height: cropHeight,
        };
    };

    /** 图片首次加载时初始化裁剪框 */
    const onLoad = useCallback((img) => {
        imgRef.current = img;
        const initialCrop = getMaxCrop(img, aspect);
        setCrop(initialCrop);
    }, [aspect]);

    /** aspect 改变时自动刷新裁剪框 */
    useEffect(() => {
        if (!imgRef.current) return; // 图片未加载，不处理
        const newCrop = getMaxCrop(imgRef.current, aspect);
        setCrop(newCrop);
    }, [aspect]);

    // 裁剪函数
    const cropImage = async (image, crop, finalWidth, finalHeight, rendered = true) => {
        const canvas = document.createElement("canvas");
        canvas.width = finalWidth;
        canvas.height = finalHeight;
        const ctx = canvas.getContext("2d");

        let scaleX = 1;
        let scaleY = 1;

        if (rendered) {
            // 渲染在页面上的主图
            scaleX = image.naturalWidth / image.width;
            scaleY = image.naturalHeight / image.height;
        }

        ctx.drawImage(
            image,
            Math.floor(crop.x * scaleX),
            Math.floor(crop.y * scaleY),
            finalWidth,
            finalHeight,
            0,
            0,
            finalWidth,
            finalHeight
        );

        return canvas.toDataURL("image/jpeg", 0.9);
    };

    const loadImage = (src) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => resolve(img);
            img.src = src;
        });
    };


    const handleConfirm = async () => {
        if (aspect === null) return false
        if (!completedCrop || !imgRef.current) return;

        const img = imgRef.current;

        const scaleX = img.naturalWidth / img.width;
        const scaleY = img.naturalHeight / img.height;

        let finalWidth = completedCrop.width * scaleX;
        let finalHeight = completedCrop.height * scaleY;

        // 取整
        finalWidth = Math.round(finalWidth);
        finalHeight = Math.round(finalHeight);

        // 严格按比例修正
        const targetRatio = aspect;

        if (finalWidth / finalHeight > targetRatio) {
            finalWidth = Math.round(finalHeight * targetRatio);
        } else {
            finalHeight = Math.round(finalWidth / targetRatio);
        }

        // 最小边约束
        if (Math.min(finalWidth, finalHeight) < 500) {
            message.error("裁剪区域太小，最小边不得小于500px！");
            return;
        }

        /** 裁主图 */
        const base64 = await cropImage(img, completedCrop, finalWidth, finalHeight, true);

        /** 如果是预设背景有不带人的refImage，再裁一次 */
        let refBase64 = null;
        if (refImage) {
            const refImg = await loadImage(refImage);
            refBase64 = await cropImage(refImg, completedCrop, finalWidth, finalHeight, false)
        }

        return {
            base64,
            width: finalWidth,
            height: finalHeight,
            refBase64 // 预设背景不带人物的背景
        };
    }

    // 禁止裁剪框缩小于500
    const MIN_SIZE = 500;
    const onCropChange = (newCrop) => {
        // 图还没加载好
        if (!imgRef.current || !newCrop.width || !newCrop.height) {
            setCrop(newCrop);
            return;
        }

        const img = imgRef.current;

        const scaleX = img.naturalWidth / img.width;
        const scaleY = img.naturalHeight / img.height;

        const realWidth = newCrop.width * scaleX;
        const realHeight = newCrop.height * scaleY;
        if (Math.min(realWidth, realHeight) < MIN_SIZE) {
            console.log(Math.min(realWidth, realHeight))
            return;
        }

        setCrop(newCrop);
    };


    return (
        <div className={styles.ImageCropperWrap}>
            <div className={styles.ImageCropper}>
                <ReactCrop
                    crop={crop}
                    onChange={onCropChange}
                    onComplete={(c) => setCompletedCrop(c)}
                    aspect={aspect}
                    keepSelection
                >
                    <img
                        crossOrigin="anonymous"
                        src={url}
                        onLoad={(e) => onLoad(e.currentTarget)}
                    />
                </ReactCrop>
            </div>
        </div>
    );
});

export default ImageCropper;
